// API client using native fetch with session cookie support

// API base URL - empty for same-origin (Flask serves SPA), or set VITE_API_URL for external backend
// In dev, Vite proxies /api to Flask. In prod with Pages, set VITE_API_URL to Cloudflare Tunnel URL.
const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface RequestOptions extends RequestInit {
  params?: Record<string, string>
}

class APIClient {
  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { params, ...fetchOptions } = options

    // Build URL with query params
    let url = `${API_BASE_URL}${endpoint}`
    if (params) {
      const queryString = new URLSearchParams(params).toString()
      url += `?${queryString}`
    }

    // Default options
    const config: RequestInit = {
      credentials: 'include', // CRITICAL: Include session cookies
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
      ...fetchOptions,
    }

    const response = await fetch(url, config)

    // Handle 401 by redirecting to login
    if (response.status === 401) {
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }

    // Handle non-2xx responses
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }))
      throw new Error(error.message || 'Request failed')
    }

    return response.json()
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params })
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async del<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`

    const config: RequestInit = {
      credentials: 'include',
      method: 'POST',
      body: formData,
      // Don't set Content-Type - browser will set it with boundary
    }

    const response = await fetch(url, config)

    // Handle 401 by redirecting to login
    if (response.status === 401) {
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }

    // Handle non-2xx responses
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }))
      throw new Error(error.message || 'Upload failed')
    }

    return response.json()
  }
}

export const apiClient = new APIClient()

// TTS API endpoints
import type {
  TTSResult,
  TTSGenerateRequest,
  TTSEnginesResponse,
  VoiceboxStatus,
  TTSVoice,
} from './types'

export const ttsApi = {
  /** Get available TTS engines and their voices */
  getEngines: (): Promise<TTSEnginesResponse> =>
    apiClient.get('/api/v4/audio/tts/engines'),

  /** Generate speech from text */
  generate: (request: TTSGenerateRequest): Promise<{ success: boolean } & TTSResult> =>
    apiClient.post('/api/v4/audio/tts/generate', request),

  /** Check Voicebox desktop app status */
  getVoiceboxStatus: (): Promise<{ success: boolean } & VoiceboxStatus> =>
    apiClient.get('/api/v4/audio/tts/voicebox/status'),

  /** Get available Voicebox voice profiles */
  getVoiceboxProfiles: (): Promise<{ success: boolean; profiles: TTSVoice[] }> =>
    apiClient.get('/api/v4/audio/tts/voicebox/profiles'),
}
